import * as docx from 'docx'
import { EventEmitter } from 'events'

/**
 * CurieEventEmitter is a singleton class that manages event listeners for the app.
 */
export class CurieEventEmitter extends EventEmitter {
  static #createdAppEvents: Map<AppEvents, (...args: unknown[]) => void> = new Map()

  globalOn(event: AppEvents, listener: (...args: unknown[]) => void) {
    if (CurieEventEmitter.#createdAppEvents.has(event)) {
      const oldListener = CurieEventEmitter.#createdAppEvents.get(event)
      if (oldListener != null) {
        this.off(event, oldListener)
      }
    }
    this.on(event, listener)
    CurieEventEmitter.#createdAppEvents.set(event, listener)
  }

  emit(event: AppEvents, ...args: unknown[]) {
    return super.emit(event, ...args)
  }
}

export const globalEventEmitter = new CurieEventEmitter()
globalEventEmitter.setMaxListeners(1024)

export enum AppEvents {
  ScrollMessagesToBottom = 'ScrollMessagesToBottom',
  setActiveTab = 'setActiveTab',
  CreateTask = 'CreateTask',
  CreateTaskJSON = 'CreateTaskJSON', // NEW: for simplified JSON submission
  TaskSubmitted = 'TaskSubmitted',
  SubmitTask = 'SubmitTask',
  SocketEvent = 'SocketEvent',
  ReviewCommentsUpdated = 'ReviewCommentsUpdated',
  OpenModal = 'OpenModal',
  CloseModal = 'CloseModal',
  OpenDocCommentPopover = 'OpenDocCommentPopover',
  OpenDocCommentPopoverCallback = 'OpenDocCommentPopoverCallback',
  SetTextAreaValue = 'SetTextAreaValue',
  SubmitTutorial = 'SubmitTutorial',
  OpenCommand = 'OpenCommand',
  CloseCommand = 'CloseCommand',
  SubmitFixedPromptTask = 'SubmitFixedPromptTask',
  SendMessage = 'SendMessage',
  SWITCH_VERSION = 'switch_version',
  VERSION_SWITCHED = 'version_switched',
  IndexesUpdated = 'IndexesUpdated',
  BillingInfoUpdated = 'BillingInfoUpdated',
  LicenseUpdated = 'LicenseUpdated',
  OpenUpgradeModal = 'OpenUpgradeModal',
  OpenQuotaExceededModal = 'OpenQuotaExceededModal',
  MemberRolesUpdated = 'MemberRolesUpdated',
  // Generic API error event - handlers can subscribe to handle specific error types
  ApiForbiddenError = 'ApiForbiddenError'
}

// API error types that can be received from backend
export enum ApiErrorType {
  UPGRADE_REQUIRED = 'upgrade_required',
  QUOTA_EXCEEDED_OVERAGE_DISABLED = 'quota_exceeded_overage_disabled',
  BILLABLE_FLOWS_EXCEEDED_OVERAGE_DISABLED = 'billable_flows_exceeded_overage_disabled',
  WORKSPACE_READ_ONLY = 'workspace_read_only',
  INSUFFICIENT_CREDITS = 'insufficient_credits'
}

export interface ApiForbiddenErrorPayload {
  errorType?: string
  message?: string
  url: string
}

/**
 * Extract error message from API error response.
 * Handles both string and structured error formats:
 * - { detail: "error message" }
 * - { detail: { error: "type", message: "error message" } }
 */
export function extractApiErrorMessage(
  errorData: unknown,
  fallbackMessage = 'An error occurred'
): string {
  if (!errorData || typeof errorData !== 'object') {
    return fallbackMessage
  }

  const data = errorData as Record<string, unknown>

  // Handle { detail: ... } format
  if (data.detail !== undefined) {
    if (typeof data.detail === 'string') {
      return data.detail
    }
    if (typeof data.detail === 'object' && data.detail !== null) {
      const detail = data.detail as Record<string, unknown>
      if (typeof detail.message === 'string') {
        return detail.message
      }
      if (typeof detail.error === 'string') {
        return detail.error
      }
    }
  }

  // Handle { message: ... } format
  if (typeof data.message === 'string') {
    return data.message
  }

  // Handle { error: ... } format
  if (typeof data.error === 'string') {
    return data.error
  }

  return fallbackMessage
}

/**
 * Read a Response body without throwing on non-JSON content.
 *
 * Auth endpoints can fail with a non-JSON body — e.g. an unhandled 500 returns
 * the bare text "Internal Server Error". Calling `response.json()` on that throws
 * a confusing `Unexpected token 'I' ... is not valid JSON` that masks the real
 * failure. This reads the body once and parses defensively: valid JSON is returned
 * as-is; non-JSON text is wrapped as `{ detail: text }` so `extractApiErrorMessage`
 * surfaces it; an empty or unreadable body yields `{}`.
 */
export async function parseResponseBody(response: Response): Promise<unknown> {
  let text = ''
  try {
    text = await response.text()
  } catch {
    return {}
  }
  if (!text.trim()) {
    return {}
  }
  try {
    return JSON.parse(text)
  } catch {
    return { detail: text }
  }
}

/**
 * Extract both error message and error type from API error response.
 * Use when callers need the error type for branching (e.g. chat error handling).
 */
export function extractApiError(
  errorData: unknown,
  fallbackMessage = 'An error occurred'
): { message: string; errorType?: string } {
  const message = extractApiErrorMessage(errorData, fallbackMessage)

  if (errorData && typeof errorData === 'object') {
    const data = errorData as Record<string, unknown>
    if (typeof data.detail === 'object' && data.detail !== null) {
      const detail = data.detail as Record<string, unknown>
      if (typeof detail.error === 'string') {
        return { message, errorType: detail.error }
      }
    }
  }

  return { message }
}

interface Inputs {
  html: string
  markdown?: string
  doc?: docx.Document
  dwl?: string
}

export async function saveAsFile(inputs: Inputs, contentType = 'text/html') {
  const { markdown, html, doc, dwl } = inputs
  function getFilename(content: string, ext: string) {
    const titleMatch = content.match(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/i)
    if (titleMatch) {
      const titleText = titleMatch[1]
        .replace(/<[^>]+>/g, '')
        .trim()
        .replace(/\s+/g, '-')
        .toLowerCase()
      return titleText + '.' + ext
    }

    const textContent = content.replace(/<[^>]+>/g, '').trim()
    const firstWords = textContent.split(/\s+/).slice(0, 5).join('-').toLowerCase()
    if (firstWords.length > 0) {
      return firstWords + '.' + ext
    }

    const date = new Date().toISOString().split('T')[0]
    return `doc-${date}.${ext}`
  }

  let blob
  let ext = 'html'
  if (contentType === 'text/dwl') {
    blob = new Blob([dwl!], { type: 'text/plain' })
    ext = 'dwl'
  } else if (contentType === 'text/markdown') {
    blob = new Blob([markdown!], { type: 'text/markdown' })
    ext = 'md'
  } else if (
    contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
  ) {
    blob = await docx.Packer.toBlob(doc!)
    ext = 'docx'
  } else {
    blob = new Blob([addCssToHtml(html)], { type: contentType })
  }

  let filename = getFilename(html, ext)
  if (ext === 'dwl') {
    filename = getFilename('script', ext)
  }
  const a = document.createElement('a')
  a.href = URL.createObjectURL(blob)
  a.download = filename
  a.click()
  URL.revokeObjectURL(a.href)
}

export function csvToMarkdown(csv: string): string {
  const rows = csv.split('\n').map(row => row.split(','))
  const headers = rows[0]
  const table = []

  // Create header row
  table.push(`| ${headers.join(' | ')} |`)
  table.push(`| ${headers.map(() => '---').join(' | ')} |`)

  // Create data rows
  for (let i = 1; i < rows.length; i++) {
    table.push(`| ${rows[i].join(' | ')} |`)
  }

  return table.join('\n')
}

export function sleep(msec: number = 1e3) {
  return new Promise(resolve => {
    setTimeout(resolve, msec)
  })
}

export function getFileBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const base64 = reader.result as string
      const base64WitoutPrefix = base64.split(',')[1]
      resolve(base64WitoutPrefix)
    }
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

export function svg2base64(svg: string, width = 1200): string {
  const svgWithBg = svg.replace(
    /<svg([^>]+)>/,
    `<svg$1 width="${width}" preserveAspectRatio="xMidYMid meet">
    <rect width="100%" height="100%" fill="white"/>`
  )
  return `data:image/svg+xml;base64,${btoa(
    encodeURIComponent(svgWithBg).replace(/%([0-9A-F]{2})/g, (match, p1) =>
      String.fromCharCode(parseInt(p1, 16))
    )
  )}`
}

export function toCamelCase(str: string): string {
  str = str.replace(/_/g, ' ')
  return str
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

export function extractDataWeaveCode(markdownContent: string): string {
  const codeBlockRegex = /```[\s\S]*?```/g
  const codeBlocks = markdownContent.match(codeBlockRegex)

  if (codeBlocks && codeBlocks.length > 0) {
    let dataWeaveCode = codeBlocks[0]

    dataWeaveCode = dataWeaveCode.replace(/```[\s\S]?|```$/g, '')

    dataWeaveCode = dataWeaveCode.replace(/^dw 2\.0\n/, '')

    return dataWeaveCode.trim()
  }

  return markdownContent
}

/**
 * @description Add CSS to the HTML content, if the HTML content does not have a head tag
 * it will add the head tag and the CSS to the head tag.
 *
 * @param html string
 * @returns string
 */
export function addCssToHtml(htmlContent: string): string {
  const hasDoctype = htmlContent.includes('<!DOCTYPE')
  const hasHtmlTag = htmlContent.includes('</html>')
  const hasHeadTag = htmlContent.includes('</head>')
  const hasBodyTag = htmlContent.includes('</body>')

  let fullHtmlContent = htmlContent
  const css = `
body {
  font-family: sans-serif;
  line-height: 1.6;
  color: #333;
  padding: 20px;
}

h1, h2, h3, h4, h5, h6 {
  color: #333;
  margin: 1em 0 0.5em;
}

p {
  margin: 0.5em 0;
}

a {
  color: #0066cc;
}

ul, ol {
  padding-left: 20px;
}

li {
  margin: 0.3em 0;
}

blockquote {
  padding-left: 1em;
  border-left: 3px solid #ddd;
  color: #555;
}

code {
  font-family: monospace;
  background-color: #f4f4f4;
  padding: 2px 4px;
}

pre {
  background-color: #f4f4f4;
  padding: 10px;
  overflow-x: auto;
}

img {
  width: auto;
  height: auto;
}

table {
  border-collapse: collapse;
  width: 100%;
  margin: 1em 0;
}

th, td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}`

  if (!hasBodyTag) {
    fullHtmlContent = `<body>${fullHtmlContent}</body>`
  }
  if (!hasHeadTag) {
    fullHtmlContent = `<head><style>${css}</style></head>${fullHtmlContent}`
  } else {
    fullHtmlContent = fullHtmlContent.replace('</head>', `<style>${css}</style></head>`)
  }
  if (!hasHtmlTag) {
    fullHtmlContent = `<html>${fullHtmlContent}</html>`
  }
  if (!hasDoctype) {
    fullHtmlContent = `<!DOCTYPE html>${fullHtmlContent}`
  }

  return fullHtmlContent
}

export function extractDWLCode(content: string): string {
  const dwPattern = /(?:%dw\s+2\.0\s*[\s\S]*?(?:---|$)[\s\S]*?)(?=(?:%dw|$))/g
  const matches = content.match(dwPattern)
  if (matches && matches.length > 0) {
    return matches[0].trim()
  }
  if (content.trim().startsWith('%dw')) {
    return content.trim()
  }
  return ''
}
